import { dsClass } from '../../common/constants/dsClasses';
import { isDeviceEppSubcategory } from '../../common/enums.js';
import { t, vatPercentageMsg } from '../../common/i18n';
import { getLocalizedString, isMandatoryAddon } from '../../common/utils/productOfferingsUtils.js';
import { PriceType } from '../HorizontalSubscriptionCard/types.js';
import { getProductOfferAddOnPrices } from '../PlaceOrder/utils/placeOrderUtilsCoCo.js';
import { ProductOfferAddOn } from './ProductOfferAddOnCoCo.js';
import type { ProductOfferingCartItems } from '../../common/types/shoppingBasketCoCo.js';
import type { Cardinality } from '../../generated/api/cardinality.js';
import type { LocalizedField } from '../../generated/api/localizedField.js';
import type { ProductOfferingPrice } from '../../generated/api/productOfferingPrice.js';
import type { ReactNode } from 'react';

export interface CommercialAddonOffering {
  id: string;
  name: LocalizedField;
  description?: LocalizedField;
  isChecked: boolean;
  isDefault?: boolean;
  cardinality?: Cardinality;
  productOfferingPrice?: ProductOfferingPrice[];
}

export interface CommercialAddonsSectionCoCoProps<T extends CommercialAddonOffering> {
  addons: T[];
  onAddOnChange: (id: string, checked: boolean) => void;
  subcategory?: string;
  quantity?: number;
  getPriceInEuros?: (addon: T) => number;
  productOffering?: ProductOfferingCartItems;
  quantityAbovePrice?: boolean;
  getAddonContent?: (addon: T) => ReactNode;
  className?: string;
  itemClassName?: string;
}

const getRecurringPriceInEuros = (addon: CommercialAddonOffering): number =>
  addon.productOfferingPrice?.find(p => p.priceType === PriceType.RECURRING_CHARGE)?.priceWithoutTax ?? 0;

export const CommercialAddonsSectionCoCo = <T extends CommercialAddonOffering>({
  addons,
  onAddOnChange,
  subcategory,
  quantity = 1,
  getPriceInEuros = getRecurringPriceInEuros,
  productOffering,
  quantityAbovePrice,
  getAddonContent,
  className,
  itemClassName = dsClass.MARGIN_TOP_2,
}: CommercialAddonsSectionCoCoProps<T>) => {
  if (addons.length === 0) {
    return null;
  }

  const isDeviceOrEppOffering = isDeviceEppSubcategory(subcategory);

  return (
    <div className={className}>
      <div className={`${dsClass.FONT_WEIGHT_700} ${dsClass.MARGIN_BOTTOM_2} ${dsClass.MARGIN_TOP_4}`}>
        {isDeviceOrEppOffering ? t.UU5G('Select additional services') : t.LXSR('Commercial add-ons')}
      </div>
      {addons.map(addon => (
        <div className={itemClassName} key={addon.id}>
          <ProductOfferAddOn
            id={addon.id}
            linkInfo={{
              id: addon.id,
              linkInfoDescription: addon.description ? getLocalizedString(addon.description) : '',
            }}
            initialValue={addon.isChecked}
            handleAddOnChange={(id: string, checked: boolean) => onAddOnChange(id, checked)}
            productOffering={productOffering}
            priceInfo={
              isDeviceOrEppOffering
                ? {
                    ...getProductOfferAddOnPrices(getPriceInEuros(addon) * quantity),
                    additionalText: t.A0OJ(vatPercentageMsg, '0'),
                  }
                : getProductOfferAddOnPrices(getPriceInEuros(addon))
            }
            title={getLocalizedString(addon.name)}
            disabled={addon.isDefault ?? isMandatoryAddon(addon)}
            hasQuantity={isDeviceOrEppOffering}
            quantity={quantity}
            quantityAbovePrice={quantityAbovePrice}
            addonContent={getAddonContent?.(addon)}
          />
        </div>
      ))}
    </div>
  );
};
